/**
 * Revolut's checkout script, loaded once per page, in the mode of the order it's for: the live copy, or Revolut's
 * sandbox copy for an order made in Sandbox mode (Revolut Pay's payments module only works with the matching copy).
 * A page that already has the other copy (the mode was switched since) loads afresh.
 */
window.loadRevolutCheckout = (() => {
    let scriptLoadPromise = null;

    return function (sandbox = false) {
        const mode = sandbox ? 'sandbox' : 'prod';
        const loaded = document.getElementById('revolut-checkout');

        if (loaded && loaded.dataset.mode !== mode) {
            window.location.reload();
            return new Promise(() => {});
        }

        if (loaded) {
            return scriptLoadPromise || Promise.resolve();
        }

        scriptLoadPromise = new Promise((resolve, reject) => {
            const script = Object.assign(document.createElement('script'), {
                src: sandbox ? 'https://sandbox-merchant.revolut.com/embed.js' : 'https://merchant.revolut.com/embed.js',
                id: 'revolut-checkout',
                async: true,
                // A classic script (it's a plain bundle): as a module it would need cross-origin headers, which
                // Revolut's sandbox server doesn't send
                onload: resolve,
                onerror: reject,
            });
            script.dataset.mode = mode;

            document.head.appendChild(script);
        });

        return scriptLoadPromise;
    };
})();

/** Revolut's checkout for an order (its token), in the mode the order was made in. */
window.revolutCheckout = (token, sandbox) => loadRevolutCheckout(sandbox).then(() => RevolutCheckout(token, sandbox ? 'sandbox' : 'prod'));

/** What every Revolut way to pay does once it's done: paid, on to the order's page; not, the reason as a toast. */
window.revolutOutcome = (orderUrl) => ({
    onSuccess() {
        Toaster.success('Payment successful.');
        window.setTimeout(() => Livewire.navigate(orderUrl), 1500);
    },
    onError(error) {
        Toaster.error(error);
    },
});

/*
 * Paying with a saved card at checkout (components/card/order/revolut/saved, Checkout::payWithSavedCard). The site asks
 * Revolut to charge the card with this browser's details; Revolut answers paid, declined, or a check by the bank: a
 * silent device check (run hidden, sandboxed) and/or a page to confirm on (shown in a dialog). Meanwhile the payment
 * is asked about every two seconds until it's paid or declined.
 */
Alpine.data('savedCards', (chosen, names = {}) => ({
    chosen,             // the card's Revolut id
    names,              // id => "Visa •••• 4242"
    busy: false,        // paying: the button waits
    challenge: null,    // the bank's page, while it's up
    timer: null,
    asked: 0,

    destroy() {
        clearTimeout(this.timer);
    },

    get label() {
        return this.busy ? 'Waiting for your bank…' : `Pay with ${this.names[this.chosen] ?? 'Card'}`;
    },

    async pay() {
        if (this.busy || !this.chosen) return;
        this.busy = true;
        this.asked = 0;

        this.handle(await this.$wire.payWithSavedCard(this.chosen, {
            time_zone_utc_offset: -new Date().getTimezoneOffset(),
            color_depth: screen.colorDepth,
            screen_width: screen.width,
            screen_height: screen.height,
            challenge_window_width: Math.min(640, window.innerWidth - 24),
        }).catch(() => null));   // the connection dropped: told, and the button works again
    },

    handle(result) {
        clearTimeout(this.timer);

        switch (result?.state) {
            case 'paid':
                this.close();
                Toaster.success('Payment successful.');
                Livewire.navigate(result.url);
                return;
            case 'challenge':
                if (this.challenge !== result.url) {
                    this.challenge = result.url;
                    this.$dispatch('dialog-bank-check', 'open');
                }
                break;
            case 'fingerprint':
                this.fingerprint(result.html);
                break;
            case 'waiting':
                break;
            default:
                this.close();
                this.busy = false;
                Toaster.error(result?.message ?? 'Revolut didn\'t take the payment. Nothing was charged; please try again.');
                return;
        }

        // Ten minutes at most, then it's left to Revolut (an abandoned check is declined)
        if (++this.asked > 300) {
            this.stop();
            return;
        }
        this.timer = setTimeout(async () => this.handle(await this.$wire.savedCardStatus(result.payment).catch(() => ({state: 'waiting', payment: result.payment}))), 2000);
    },

    /** The bank's silent device check: Revolut's HTML, run once in a hidden, sandboxed frame */
    fingerprint(html) {
        if (!html || this.$refs.fingerprint.childElementCount) return;

        const frame = Object.assign(document.createElement('iframe'), {srcdoc: html, hidden: true, title: 'Bank check'});
        frame.setAttribute('sandbox', 'allow-scripts allow-forms');
        this.$refs.fingerprint.append(frame);
    },

    /** Closed from the bank's dialog: no more asking (the payment lapses at Revolut if the check wasn't done) */
    stop() {
        clearTimeout(this.timer);
        this.close();
        this.busy = false;
    },

    close() {
        this.challenge = null;
        this.$refs.fingerprint.replaceChildren();
        this.$dispatch('dialog-bank-check', 'close');
    },
}));
